/**
 * 서버 응답을 기다리는 동안 버튼에 도는 원 + 진행 문구를 보여 주는 공통 헬퍼.
 *
 * - 버튼을 잠그고(disabled) aria-busy="true" 를 단다 — 도는 원은 common.css
 *   `.button[aria-busy='true']` 가 그린다(흐려지지 않는다).
 * - label 을 주면 글자를 그 문구로 바꾼다. label 이 없으면 글자는 그대로 두고 원만 돈다.
 * - 되돌리는 함수(release)를 돌려준다: 원래 내용(아이콘 포함) · 원래 disabled 상태로 복원한다.
 *   (되돌릴 필요 없이 성공해서 페이지를 옮기는 경우는 안 불러도 된다 — 화면이 바뀐다)
 * - button 이 없으면(null · undefined) 아무 것도 하지 않는 release 를 돌려준다.
 * - 버튼이 이미 aria-busy="true" 면(Enter 키 재제출 등으로 겹쳐 불렸을 때) 상태를 건드리지 않고
 *   아무 것도 하지 않는 release 를 돌려준다 — 안 그러면 먼저 걸린 busy 의 release 가 나중 걸린
 *   busy 의 disabled·aria-busy 를 걷어 내 버튼이 "확인 중…" + disabled 로 굳어 버린다.
 *
 * 성공 뒤 페이지를 옮기는 경로는 release 를 안 부르고 그대로 두는 경우가 많다(화면이 바뀌니까).
 * 그런데 bfcache(뒤로가기로 돌아온 페이지)는 JS 상태가 그대로 남아 있어, 도는 원 · disabled 로
 * 굳은 버튼이 그대로 보인다. 이 모듈이 아직 풀리지 않은 release 를 전부 들고 있다가, pageshow 에서
 * event.persisted 면(= bfcache 로 되돌아온 것) 한꺼번에 풀어 준다.
 *
 * @param {HTMLButtonElement | null | undefined} button 누른 버튼
 * @param {string} [label] 진행 중 글자 (예: '보내는 중…') — 생략하면 글자는 유지
 * @return {() => void} release — 원래 상태로 되돌린다
 */
const pendingReleases = new Set();
let bfcacheListenerBound = false;

const bindBfcacheRelease = () => {
  if (bfcacheListenerBound) {
    return;
  }

  bfcacheListenerBound = true;

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) {
      return;
    }

    // 각 release 가 스스로 pendingReleases 에서 빠지므로 복사본을 돈다
    Array.from(pendingReleases).forEach((release) => release());
  });
};

export const busy = (button, label) => {
  if (!button || 'true' === button.getAttribute('aria-busy')) {
    return () => {};
  }

  // 글자만이 아니라 안쪽 요소(아이콘 span 등)까지 통째로 보관했다가 되돌린다
  const original = Array.from(button.childNodes);
  const wasDisabled = button.disabled;

  button.disabled = true;
  if (label) {
    button.textContent = label;
  }
  button.setAttribute('aria-busy', 'true');

  bindBfcacheRelease();

  let released = false;

  const release = () => {
    if (released) {
      return;
    }

    released = true;
    pendingReleases.delete(release);
    button.disabled = wasDisabled;
    if (label) {
      button.replaceChildren(...original);
    }
    button.removeAttribute('aria-busy');
  };

  pendingReleases.add(release);

  return release;
};
